import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';

import { JobPosition } from '@pflegia/types/api/job-position/job-position';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { CardStatus, CardType } from '@pflegia/ui/pflegia/types/card';
import { removeCountryName } from '@pflegia/ui/shared/utils/remove-country-name';
import AnonymousWrapper from '@pflegia/ui/shared/wrapper/AnonymousWrapper';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import CareTypeWithIcon from '@components/care-type-with-icon/CareTypeWithIcon';

import SummaryItem from '@domains/job-detail/components/job-overview/SummaryItem';

import { PerksList } from '@hooks/get-job-position.hook';

import { FUNNEL } from '@shared/application/constants';
import { useRouterHelper } from '@shared/hooks/use-router-helper';

import ContactPersonCard from './ContactPerson';
import { ContactPersonTransparent } from './ContactPersonTransparent';
import defaultPerks from './DefaultJobPerks';
import SummaryCard from './SummaryCard';

export interface JobOverviewProps {
  anonymous?: boolean;
  jobPosition: JobPosition;
  perks: PerksList;
  status: CardStatus;
  type: CardType;
  openDialog: (connectionInfo: ConnectionInfo) => void;
  gallery?: string[];
  isCoverImageShown?: boolean;
}

export const JobOverview = ({
  anonymous = false,
  jobPosition,
  perks,
  status,
  type,
  openDialog,
  gallery,
  isCoverImageShown,
}: JobOverviewProps): React.JSX.Element => {
  const { t } = useTranslation(['jobDetails', 'candidateFields']);
  const { displayContactPerson } = jobPosition.employer;
  const { isTransparentPath, isExternalJob } = useRouterHelper();
  const coverImage = (gallery && gallery.length > 0 && getPublicImageUrl(gallery[0])) || '';
  return (
    <div
      data-testid='job-overview-container'
      className='grid grid-cols-1 gap-6 lg:grid-cols-10 lg:gap-x-0 lg:gap-y-6 lg:rounded-none lg:bg-white'>
      <div className='rounded-2xl bg-white lg:col-span-10 lg:rounded-none'>
        {isTransparentPath ? (
          <div className='lg:mx-12 lg:border-b lg:border-gray-200' />
        ) : (
          <div className='mx-6 grid py-4 lg:mx-12 lg:grid-cols-10 lg:border-b lg:border-gray-200'>
            <div className='hidden lg:col-span-6 lg:block'>
              <AnonymousWrapper anonymous={anonymous} href={FUNNEL}>
                <div className='grid grid-cols-2 gap-6'>
                  {perks.employer && (
                    <SummaryItem
                      data-testid={`employer-name-hidden-${String(anonymous)}`}
                      black
                      emoji={defaultPerks.employer}
                      key={t('perks.employer')}
                      label={t('perks.employer')}
                      value={perks.employer}
                    />
                  )}
                  {perks.address && (
                    <SummaryItem
                      data-testid={`employer-address-hidden-${String(anonymous)}`}
                      black
                      emoji={defaultPerks.address}
                      label={t('perks.address')}
                      value={removeCountryName(perks.address)}
                    />
                  )}
                </div>
              </AnonymousWrapper>
            </div>
            <div className='flex lg:col-span-4 lg:justify-end'>
              <CareTypeWithIcon type={jobPosition.employer.careType} />
            </div>
          </div>
        )}
      </div>

      <div className='border-gray-200 lg:col-span-6 lg:mb-8 lg:border-r'>
        <SummaryCard
          anonymous={anonymous}
          careType={jobPosition.employer.careType}
          formalLanguage={jobPosition.employer.formalLanguage}
          perks={perks}
        />
      </div>

      <div className={clsx('lg:col-span-4 lg:mb-8', jobPosition.isInitiative ? 'hidden px-5 md:block' : '')}>
        {isCoverImageShown && (
          <div className='relative h-28 md:h-72'>
            <Image
              alt=''
              className='rounded-2xl object-contain lg:object-cover'
              fill
              src={coverImage}
              sizes='(max-width: 768px) 100vw, 40vw'
            />
          </div>
        )}

        {!isCoverImageShown &&
          (isTransparentPath && !isExternalJob ? (
            <ContactPersonTransparent
              contactPerson={displayContactPerson}
              jobPositionId={jobPosition.id}
              jobPositionTitle={jobPosition.title}
              employerName={jobPosition.employer.name}
              openDialog={openDialog}
              jobPositionSlug={jobPosition.slug}
            />
          ) : (
            <ContactPersonCard
              anonymous={anonymous}
              contactPerson={displayContactPerson}
              jobPositionId={jobPosition.id}
              jobPositionTitle={jobPosition.title}
              // @ts-expect-error Mismatching types
              employer={jobPosition.employer}
              status={status}
              type={type}
              openDialog={openDialog}
            />
          ))}
      </div>
    </div>
  );
};

export default JobOverview;
